import { ChangeDetectionStrategy, Component, OnInit, inject, computed, OnDestroy, Signal, signal, forwardRef } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NotebookVariablesService } from '../../services/notebook-variables/notebook-variables.service';
import { ProjectVariablesService } from '../../services/project-variables/project-variables.service';
import _ from 'lodash';
import { GeneralHelpers } from '../../helpers/general.helper';
import { GlobalVar } from '../../interfaces/global-var.interface';
import { MessageService } from '../../services/message/message.service';
import { Subscription } from 'rxjs';
import { ProjectConstant } from '../../interfaces/project-constants.interface';
import {
  EDIT_GLOBAL_VAR,
  EDIT_PROJECT_CONSTANT,
  INVALIDATE_CHUNKS,
} from '../../constants/general.constants';
import { AppStateService } from '../../services/app-state/app-state.service';
import { PermissionId } from '../../constants/permissions.constants';
import { ProjectService } from '../../services/project/project.service';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CodeEditorComponent } from '../code-editor/code-editor.component';

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-global-var-dialog',
  templateUrl: './global-var-dialog.component.html',
  styleUrls: ['./global-var-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    // Material
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    // Layout
    FlexLayoutModule,
    // Form
    ReactiveFormsModule,
    FormsModule,
    // Ngx Mask
    // Code Editor — forwardRef breaks an NG0919 circular import: this dialog is
    // opened by DialogService, and CodeEditorComponent depends (via DialogService
    // and CodeMirror's injected services) back on DialogService. Without the
    // deferred reference, CodeEditorComponent is undefined when this dialog's
    // component def is built, blanking the dialog behind its backdrop (SCRUM-689).
    forwardRef(() => CodeEditorComponent),
  ],
})
export class GlobalVarDialogComponent implements OnInit, OnDestroy {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<GlobalVarDialogComponent>>(MatDialogRef);
  private _formBuilder = inject(FormBuilder);
  private notebookVariablesService = inject(NotebookVariablesService);
  private projectVariablesService = inject(ProjectVariablesService);
  private messageService = inject(MessageService);

  #appState = inject(AppStateService);
  #projectService = inject(ProjectService);

  hasWritePermission = computed(() =>
    this.#projectService.getCurrentUserHasProjectPermission(PermissionId.WRITE)
  );

  public globalVarForm!: FormGroup;
  public workMode = signal<'create' | 'update'>('create');
  public serviceToUse: 'notebookVariablesService' | 'projectVariablesService' =
    'notebookVariablesService';
  public messageSubscription!: Subscription;
  public generalHelpers = GeneralHelpers;
  public content = signal('');
  private stepId!: number | any;
  public isMainVersion: boolean = this.#appState.isHeadVersion() ?? false;
  public showEmptyValueError = signal(false);

  // Reactive variable list — reads from the full list signal so any mutation
  // (add, update, delete) triggers recomputation, not just length changes.
  public varList: Signal<(GlobalVar | ProjectConstant)[]> = computed(() => {
    if (this.serviceToUse === 'notebookVariablesService') {
      return this.notebookVariablesService.variableList$();
    } else {
      return this.projectVariablesService.variableList$();
    }
  });

  constructor() {
    this.updateServiceUsage();
  }

  ngOnInit() {
    this.initForm();
    this.subscribeToMessages();

    const variableToEdit =
      this.data?.variable ?? this.data?.globalVar ?? this.data?.projectConstant;
    if (variableToEdit) {
      this.updateForm(variableToEdit);
    }
  }


  ngOnDestroy() {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
  }

  private initForm() {
    // Only disable the form if we're in a version AND trying to create a new variable
    const shouldDisableForm = !this.isMainVersion && this.workMode() === 'create';

    this.globalVarForm = this._formBuilder.group({
      name: [
        { value: '', disabled: shouldDisableForm },
        [
          Validators.required,
          // Allow letters (both cases), numbers, and symbols except spaces
          Validators.pattern(/^[a-zA-Z_$][a-zA-Z0-9_$]*$/),
        ],
      ],
      id: [],
    });
  }

  private subscribeToMessages() {
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (
          message?.text === EDIT_GLOBAL_VAR ||
          message?.text === EDIT_PROJECT_CONSTANT
        ) {
          this.updateForm(message?.data?.variable);
        }
      });
  }

  public processGlobalVarForm(formValue: any, isValid: boolean, $event: Event) {
    $event.preventDefault();

    const contentValue = this.content();
    const contentIsEmpty = !contentValue || contentValue.trim().length === 0;
    this.showEmptyValueError.set(contentIsEmpty);

    if (!isValid || contentIsEmpty) {
      return;
    }

    if (!this.isUniqueName(formValue.name) && this.workMode() !== 'update') {
      this.globalVarForm.controls['name'].setErrors({ notUnique: true });
      return;
    }

    const id = formValue.id || GeneralHelpers.getUuid();

    let value;
    try {
      // Try to parse the content as JSON first
      value = JSON.parse(contentValue);
    } catch {
      // If it's not valid JSON, try to infer the type
      const trimmedContent = contentValue.trim();
      if (trimmedContent === 'true') {
        value = true;
      } else if (trimmedContent === 'false') {
        value = false;
      } else if (trimmedContent === 'null') {
        value = null;
      } else if (trimmedContent === 'undefined') {
        value = undefined;
      } else if (!isNaN(Number(trimmedContent))) {
        value = Number(trimmedContent);
      } else {
        value = contentValue; // Keep as string if no other type matches
      }
    }

    const processGlobalVarForm: any = {
      name: this.globalVarForm.getRawValue().name,
      value: value,
      id: id,
      sortOrder: 0,
    };

    if (this.workMode() === 'create') {
      this[this.serviceToUse].add(processGlobalVarForm);
    } else {
      this[this.serviceToUse].update(id, processGlobalVarForm);
    }

    if (this.stepId) {
      this.messageService.sendMessage(INVALIDATE_CHUNKS, [this.stepId]);
      this.stepId = null;
    }

    this.resetForm();
  }

  public edit(item: GlobalVar | any) {
    this.workMode.set('update');
    this[this.serviceToUse].edit(item.id);
    // varList signal will automatically update
  }

  public delete(item: GlobalVar | any) {
    this[this.serviceToUse].delete(item.id);
    // varList signal will automatically update
    this.resetForm();
  }

  private isUniqueName(name: string): boolean {
    return !_.some(
      this[this.serviceToUse].variableList,
      (item) =>
        item.name === name && item.id !== this.globalVarForm.get('id')?.value
    );
  }

  private updateForm(data: GlobalVar | ProjectConstant) {
    this.workMode.set('update');
    this.globalVarForm.patchValue({
      name: data.name,
      id: data.id,
    });
    this.content.set(this.setContent(data));
  }

  private resetForm() {
    this.workMode.set('create');
    this.content.set('');
    this.showEmptyValueError.set(false);
    this.globalVarForm.reset();
    Object.values(this.globalVarForm.controls).forEach((control) => {
      control.setErrors(null);
      control.markAsUntouched();
      control.markAsPristine();
    });
  }

  public updateContent(data: any) {
    this.content.set(data);
    if (data && data.trim().length > 0) {
      this.showEmptyValueError.set(false);
    }
  }

  private updateServiceUsage() {
    if (this.data?.serviceToUse !== undefined) {
      this.serviceToUse = this.data.serviceToUse;
    }
  }

  public setFormTouched() {
    this.globalVarForm.markAsTouched();
  }

  private setContent(data: any): string {
    if (data?.value === undefined || data?.value === null) {
      return '';
    }

    let value = data.value;
    if (typeof value === 'string') {
      try {
        const parsedValue = JSON.parse(value);
        value = parsedValue;
      } catch {
        // If it's not valid JSON, keep it as a string
      }
    }

    const type = GeneralHelpers.trueTypeOf(value);
    if (type === 'object' || type === 'array') {
      this.setChunkId(data);
      return GeneralHelpers.jsonStringify(value);
    }
    return value.toString();
  }

  private setChunkId(data: any) {
    if (data?.stepId) {
      this.stepId = data.stepId;
    }
  }

  public getActionButtonText(): string {
    return this.workMode() === 'update' ? 'Update' : 'Add';
  }

  /**
   * Workflow variables can be saved to browser cache without project WRITE.
   * Project constants still require WRITE on HEAD.
   */
  confirmButtonDisabled(): boolean {
    if (!this.isMainVersion && this.workMode() === 'create') {
      return true;
    }
    if (
      this.serviceToUse === 'projectVariablesService' &&
      this.isMainVersion &&
      !this.hasWritePermission()
    ) {
      return true;
    }
    return false;
  }
}
